import api from 'src/utils/ajax';

import { fetchParentResources } from '../fbm-resource/fbm-resource';
import { ThumbnailAsset } from '../resource/_types';

import { Service } from './_types';
import { denormalizeService, normalizeService } from './normalizers';

import type { Identifier } from '../../types/utilityTypes';

export const fetchServicesOptions = async (): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
    },
  });

  return response.data.map(normalizeService);
};

interface FetchServicesOptions {
  includePrivate?: boolean;
  sort?: string;
}

export const fetchServices = async ({
  includePrivate = false,
  sort,
}: FetchServicesOptions): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      ...(includePrivate && { include_private: true }),
      ...(sort && { q: { s: sort } }),
    },
  });

  return response.data.map((a) => normalizeService(a));
};

interface FetchServiceListOptions {
  page?: number;
  per?: number;
  sortBy?: string;
  sortDir?: string;
  defaultTime?: string;
  search?: string;
  serviceType?: string;
  tags?: string[];
  deleted?: boolean;
}

export const fetchServiceList = async ({
  page,
  per,
  sortBy,
  sortDir,
  defaultTime,
  search,
  serviceType,
  tags,
  deleted,
}: FetchServiceListOptions): Promise<{
  total: number;
  services: Service[];
}> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      for_user: true,
      page,
      per,
      ...(deleted && { deleted: true }),
      q: {
        default_time_eq: defaultTime,
        name_cont: search,
        service_type_eq: serviceType,
        ...(sortBy && {
          s: `${sortBy} ${sortDir || 'desc'}`,
        }),
      },
      ...(tags && { 'q[tags_name_eq_any]': tags }),
    },
  });

  return {
    total: response.data.meta.total,
    services: response.data.service.map((a) => normalizeService(a)),
  };
};

export const createService = async (
  service: Partial<Service>
): Promise<Service> => {
  const response = await api.post(`/api/v4/fbm/services`, {
    service: denormalizeService(service),
  });
  return normalizeService(response.data);
};

export const updateService = async (
  service: Partial<Service>
): Promise<Service> => {
  const response = await api.put(`/api/v4/fbm/services/${service.id}`, {
    service: denormalizeService(service),
  });
  return normalizeService(response.data);
};

export const updateServiceTagList = async (
  id: Service['id'],
  tags: Service['tags']
): Promise<void> => {
  await api.put(`/api/v4/fbm/services/${id}`, {
    service: { tag_list: tags.join(',') },
  });
};

export const deleteService = async (id: Service['id']): Promise<Service> => {
  const response = await api.delete(`/api/v4/fbm/services/${id}`);
  return normalizeService(response.data);
};

export const fetchServiceOptions = async (params = {}): Promise<Service[]> => {
  const response = await api.get(`/api/v4/fbm/services`, {
    params: {
      fetch_all: true,
      'q[s]': 'name asc',
      ...params,
    },
  });

  return response.data.map((a) => normalizeService(a));
};

export const saveServiceConnection = async (data) => {
  const response = await api.post(`/api/v4/fbm/service_connections`, {
    service_connection: denormalizeService(data),
  });

  return response.data;
};

export const createServiceThumbnail = async (
  file: File
): Promise<ThumbnailAsset> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);

  const response = await api.post('/api/v2/assets/image/service', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

export const fetchService = async (
  serviceId: Identifier,
  relatedSubResources?: boolean
): Promise<Service> => {
  const response = await api.get(`/api/v4/fbm/services/${serviceId}`);
  const service = normalizeService(response.data);
  if (relatedSubResources) {
    const promises = service.serviceResources.map(async (sr) => {
      const subResources = await fetchParentResources({
        'q[parent_id_eq]': sr.resource.id,
      });
      return {
        ...sr,
        resource: { ...sr.resource, subResources },
      };
    });
    const serviceResources = await Promise.all(promises);
    service.serviceResources = serviceResources;
  }
  return service;
};

export const cloneService = async (
  serviceId: Identifier,
  name?: string
): Promise<Service> => {
  const response = await api.post(`/api/v4/fbm/services/${serviceId}/clone`, {
    name,
  });
  return normalizeService(response.data);
};
